/** @format */
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    PillLabel,
    PillLabelSizeVariant,
    PillLabelVariant,
} from '@mpb-com/feather/components/atoms/pill-label';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import {
    ConstrainedWrapper,
    ConstrainedWrapperType,
} from '@toucan/client/shared/components/constrained-wrapper';

import { DEFAULT_TEST_ID } from '../../config/constants';
import { KitIntelligenceSectionProps } from '../../config/types';
import { KitCard, KitCardLayout } from '../kit-card';
import { KitFeedback } from '../kit-feedback';
import { KitLeadCard } from '../kit-lead-card';
import { KitStatTile } from '../kit-stat-tile';
import { styles } from './kit-section.styles';

const StyledKitSection = styled.section`
    ${styles}
`;

/**
 * The two tall cards and the one wide card share one layout switch between
 * them: the first two cards on the grid are the tall ones, the third is the
 * wide one. Nothing about the data says this — the export writes `cards`
 * most common first, not by shape — so the layout is decided here, by
 * position, and would need to move with the data if the export ever
 * reordered which theme goes where.
 */
const CARD_LAYOUTS = [
    KitCardLayout.STACKED,
    KitCardLayout.STACKED,
    KitCardLayout.INLINE,
];

export const KitSection: FunctionComponent<KitIntelligenceSectionProps> = ({
    kitIntelligence,
    modelName,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    ...otherProps
}) => {
    const { t } = useTranslation();
    const { lead, cards, stats } = kitIntelligence;
    /*
     * The pills on the lead card name the themes on the grid, so a theme named
     * twice is named once here: nothing in the export stops two of the three
     * cards sharing a genre, and "Accessory, Accessory" would read as a mistake
     * rather than as two cards' worth of it. Today's export happens to have no
     * such model among the ones this section renders, which is exactly why this
     * is worth pinning down rather than leaving to chance.
     */
    const genres = [...new Set(cards.map((card) => card.genre))];

    return (
        <StyledKitSection data-testid={dataTestId} {...otherProps}>
            <ConstrainedWrapper type={ConstrainedWrapperType.PADDING}>
                <div className="kit-section-header">
                    <div className="kit-section-eyebrow-row">
                        <span className="kit-section-eyebrow">
                            {t('Kit intelligence')}
                        </span>
                        <PillLabel
                            variant={PillLabelVariant.SUBTLE}
                            size={PillLabelSizeVariant.SMALL}
                        >
                            {t('Beta')}
                        </PillLabel>
                    </div>
                    <Heading
                        size={HeadingSize.H2}
                        className="kit-section-heading"
                    >
                        {t(
                            'How MPB customers build their kit — and what it tells us'
                        )}
                    </Heading>
                    <p className="kit-section-intro">
                        {t(
                            '{{value}} of {{modelName}} buyers {{label}}. Look at which ones, and a picture starts to emerge.',
                            { value: lead.value, modelName, label: lead.label }
                        )}
                    </p>
                </div>

                <div className="kit-section-grid">
                    <KitLeadCard
                        className="kit-section-lead"
                        lead={lead}
                        genres={genres}
                        data-testid={`${dataTestId}__lead-card`}
                    />
                    {cards.map((card, index) => (
                        <KitCard
                            /*
                             * Keyed by position, not by genre: two of the three
                             * cards sharing a genre is allowed, and unlike the
                             * pills on the lead card these cannot be collapsed
                             * into one — the grid is three fixed cells, and each
                             * card holds its own kit.
                             */
                            // eslint-disable-next-line react/no-array-index-key -- Legacy code: do not use as an example.
                            key={index}
                            className={`kit-section-card kit-section-card--${index}`}
                            card={card}
                            layout={CARD_LAYOUTS[index]}
                            data-testid={`${dataTestId}__card-${index}`}
                        />
                    ))}
                    {stats.map((stat, index) => (
                        <KitStatTile
                            // eslint-disable-next-line react/no-array-index-key -- Legacy code: do not use as an example.
                            key={index}
                            className={`kit-section-stat kit-section-stat--${index}`}
                            stat={stat}
                            data-testid={`${dataTestId}__stat-${index}`}
                        />
                    ))}
                </div>

                <KitFeedback
                    modelId={kitIntelligence.modelId}
                    data-testid={`${dataTestId}__feedback`}
                />
            </ConstrainedWrapper>
        </StyledKitSection>
    );
};
